import { isPerformanceLongAnimationFrameTiming } from './helpers/isPerformanceLongAnimationFrameTiming';
import { Interaction, UserInteractionCollector } from './lib/UserInteractionCollector';
import { PerformanceLongAnimationFrameTiming, PerformanceScriptTiming } from './types';
import { INTERACTIONS_BUFFER_LIMIT, MAX_INTERACTION_DELTA, TRACKED_EVENTS } from './constants';
import { mapEventTypeToTrackedEventType } from './helpers/mapEventTypeToTrackedEventType';
import { debugLog } from '../debugLog';

export type LongFrameRecord = Pick<PerformanceLongAnimationFrameTiming, 'duration'> & {
    /** Интеракции, которые вероятно связаны с LoAF */
    relatedInteractions: Interaction[];
    /** Тайминги скриптов */
    scriptTimings: PerformanceScriptTiming[];
};

/**
 * Детектор самого длинного фрейма.
 */
export class LongestAnimationFrameObserver {
    private userEventCollector = new UserInteractionCollector(TRACKED_EVENTS, INTERACTIONS_BUFFER_LIMIT);
    private performanceObserver: PerformanceObserver;
    private longestAnimationFrame: LongFrameRecord | null = null;
    private startTime: DOMHighResTimeStamp = 0;

    constructor() {
        this.performanceObserver = new PerformanceObserver(list => {
            const entries = list.getEntries();

            let longestEntry: PerformanceEntry | null = null;
            for (const entry of entries) {
                // фильтруем все события, которые произошли до запуска детектора
                if (entry.startTime < this.startTime) {
                    continue;
                }

                if (!this.longestAnimationFrame || entry.duration > this.longestAnimationFrame.duration) {
                    longestEntry = entry;
                }
            }

            if (longestEntry && isPerformanceLongAnimationFrameTiming(longestEntry)) {
                this.handleLongAnimationFrame(longestEntry);
            }
        });
    }

    /** Получает самый длинный фрейм за все время работы  */
    getLongestAnimationFrame() {
        return this.longestAnimationFrame;
    }

    /** Удаляет запись о самом длинном фрейме */
    resetLongestAnimationFrame() {
        this.longestAnimationFrame = null;
    }

    /** Ищет интеракцию, которая предположительно является триггером скрипта */
    private findUserEventByScriptTiming(timing: PerformanceScriptTiming) {
        if (timing.invokerType !== 'event-listener') {
            return null;
        }

        // Пример: 'DIV#root.onclick', 'XMLHttpRequest.onloadend', 'MessagePort.onmessage'
        const eventType = timing.invoker.split('.').at(-1)?.substring(2);

        // Ищем по событиям, за которыми следили
        const trackedEventType = eventType && mapEventTypeToTrackedEventType(eventType);
        if (!trackedEventType) {
            return null;
        }

        const interaction = this.userEventCollector.getClosestInteraction(timing.startTime, trackedEventType);
        if (interaction && timing.startTime - interaction.timeStamp < MAX_INTERACTION_DELTA) {
            return {
                ...interaction,
                // Отправляем оригинальный тип события
                eventType,
            };
        }
        return null;
    }

    private handleLongAnimationFrame(entry: PerformanceLongAnimationFrameTiming) {
        const timings = entry.scripts;
        const interactions = new Set<Interaction>();

        for (const timing of timings) {
            // пытаемся найти пользовательское действие, которое вызвало скрипт
            const event = this.findUserEventByScriptTiming(timing);
            if (event) {
                interactions.add(event);
            }
        }

        // если не нашли ни одного триггерного события
        if (interactions.size === 0) {
            // ищем все интеракции, которые были во время фрейма, если они были
            if (entry.firstUIEventTimestamp > 0) {
                const eventsBetweenStartAndEnd = this.userEventCollector.getInteractionsBetween(
                    entry.firstUIEventTimestamp,
                    entry.startTime + entry.duration
                );
                eventsBetweenStartAndEnd.forEach(e => interactions.add(e));
            } else {
                // если интераций не было, то берем все последние интеракции
                this.userEventCollector.getAllInteractions().forEach(e => interactions.add(e));
            }
        }

        const relatedInteractions = [...interactions];

        debugLog(() => ({
            tags: ['LoAF', entry.duration.toFixed(2)],
            details: {
                timings,
                relatedEvents: relatedInteractions,
            },
        }));

        this.longestAnimationFrame = {
            duration: entry.duration,
            relatedInteractions,
            scriptTimings: timings,
        };
    }

    /**
     * Запускает детектор
     * @param requestBuffered запросить события LoAF, которые собирались до вызова этого метода
     */
    start(requestBuffered?: boolean) {
        this.startTime = requestBuffered ? 0 : performance.now();
        this.userEventCollector.start();
        this.performanceObserver.observe({ type: 'long-animation-frame', buffered: requestBuffered });
    }

    /** Останавливает детектор */
    stop() {
        this.userEventCollector.stop();
        this.performanceObserver.disconnect();
    }

    /** Освобождает задействованные ресурсы */
    dispose() {
        this.stop();
        this.userEventCollector.dispose();
    }
}
